import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Stack,
  CircularProgress,
  Avatar,
  Chip,
  Fade
} from '@mui/material';
import { Send as SendIcon, SmartToy as AIIcon, AutoAwesome as SparkleIcon, ChevronRight } from '@mui/icons-material';
import axios from '../../api/axios';
import MarkdownRenderer from '../Common/MarkdownRenderer';

function AIChat({ exampleId, question, questionType, studentAnswer, onClose }) {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const messagesEndRef = useRef(null);
  const conversationHistoryRef = useRef([]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load conversation history on mount
  useEffect(() => {
    const loadConversationHistory = async () => {
      try {
        const questionId = `notebook_${exampleId}`;
        const response = await axios.get(`/notebook-examples/chat/conversations/${questionId}`);
        const history = response.data.messages || [];
        
        if (history.length > 0) {
          setMessages(history);
          conversationHistoryRef.current = history;
        }
      } catch (error) {
        console.error('Error loading conversation history:', error);
        // Continue without history
      } finally {
        setLoadingHistory(false);
      }
    };
    
    if (exampleId) {
      loadConversationHistory();
    }
  }, [exampleId]);

  const handleSend = async () => {
    if (!inputMessage.trim() || loading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    
    // Add user message to UI
    const newUserMessage = { role: 'user', content: userMessage };
    setMessages(prev => [...prev, newUserMessage]);
    setLoading(true);

    try {
      const response = await axios.post(`/notebook-examples/${exampleId}/chat`, {
        message: userMessage,
        conversationHistory: conversationHistoryRef.current
      });

      const assistantMessage = { role: 'assistant', content: response.data.response };
      setMessages(prev => [...prev, assistantMessage]);
      conversationHistoryRef.current = response.data.conversationHistory || [];
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        role: 'assistant',
        content: '抱歉，发生了错误：' + (error.response?.data?.error || '请重试')
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Paper 
      elevation={0}
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box 
        sx={{ 
          p: 2.5, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          position: 'relative'
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pr: 4 }}>
          <Avatar 
            sx={{ 
              bgcolor: 'rgba(255,255,255,0.2)', 
              width: 40, 
              height: 40,
              backdropFilter: 'blur(10px)'
            }}
          >
            <SparkleIcon sx={{ fontSize: 24 }} />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={600}>
              AI 学习助手
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mt: 0.25 }}>
              随时为你解答疑问
            </Typography>
          </Box>
        </Stack>
        {onClose && (
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.2)',
              }
            }}
            size="small"
          >
            <ChevronRight />
          </IconButton>
        )}
      </Box>

      {/* Messages Area */}
      {loadingHistory ? (
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box 
          sx={{ 
            flexGrow: 1, 
            overflow: 'auto', 
            p: 2.5,
            bgcolor: 'grey.50',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'grey.300',
              borderRadius: '4px',
              '&:hover': {
                bgcolor: 'grey.400',
              },
            },
          }}
        >
          {messages.length === 0 && (
          <Fade in={true}>
            <Box 
              sx={{ 
                textAlign: 'center', 
                py: 6,
                px: 2
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  p: 2,
                  borderRadius: '50%',
                  bgcolor: 'primary.light',
                  mb: 3,
                  opacity: 0.1
                }}
              >
                <AIIcon sx={{ fontSize: 56, color: 'primary.main' }} />
              </Box>
              <Typography variant="h6" fontWeight={500} sx={{ mb: 1, color: '#000000' }}>
                你好！我是你的AI学习助手
              </Typography>
              <Typography variant="body2" sx={{ color: '#000000', mb: 3 }}>
                我可以帮助你理解这道题和相关知识点
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
                <Chip 
                  label="解释概念" 
                  size="small" 
                  variant="outlined"
                  sx={{ borderColor: 'primary.main', color: 'primary.main' }}
                />
                <Chip 
                  label="解题思路" 
                  size="small" 
                  variant="outlined"
                  sx={{ borderColor: 'primary.main', color: 'primary.main' }}
                />
                <Chip 
                  label="知识点" 
                  size="small" 
                  variant="outlined"
                  sx={{ borderColor: 'primary.main', color: 'primary.main' }}
                />
              </Stack>
            </Box>
          </Fade>
        )}
        
        <Stack spacing={2.5}>
          {messages.map((msg, idx) => (
            <Fade in={true} key={idx}>
              <Stack
                direction="row"
                spacing={1.5}
                justifyContent={msg.role === 'user' ? 'flex-end' : 'flex-start'}
                alignItems="flex-start"
              >
                {msg.role === 'assistant' && (
                  <Avatar 
                    sx={{ 
                      bgcolor: 'primary.main', 
                      width: 36, 
                      height: 36,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}
                  >
                    <SparkleIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                )}
                <Box
                  sx={{
                    maxWidth: '75%',
                    p: 2,
                    borderRadius: msg.role === 'user' 
                      ? '18px 18px 4px 18px' 
                      : '18px 18px 18px 4px',
                    bgcolor: msg.role === 'user' 
                      ? 'primary.main' 
                      : 'white',
                    color: msg.role === 'user' 
                      ? 'white' 
                      : '#000000',
                    boxShadow: msg.role === 'user'
                      ? '0 2px 8px rgba(25, 118, 210, 0.3)'
                      : '0 2px 8px rgba(0,0,0,0.08)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '& .MuiTypography-root, & p, & span, & div': msg.role === 'assistant' ? { color: '#000000 !important' } : {},
                    '& a': msg.role === 'assistant' ? { color: '#1976d2 !important' } : {},
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: msg.role === 'user'
                        ? '0 4px 12px rgba(25, 118, 210, 0.4)'
                        : '0 4px 12px rgba(0,0,0,0.12)',
                    }
                  }}
                >
                  {msg.role === 'assistant' ? (
                    <Box sx={{ fontSize: '0.9375rem', lineHeight: 1.6, color: '#000000 !important' }}>
                      <MarkdownRenderer content={msg.content} />
                    </Box>
                  ) : (
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.6,
                      fontSize: '0.9375rem'
                    }}
                  >
                    {msg.content}
                  </Typography>
                  )}
                </Box>
                {msg.role === 'user' && (
                  <Avatar 
                    sx={{ 
                      bgcolor: 'secondary.main', 
                      width: 36, 
                      height: 36,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}
                  >
                    <Typography variant="caption" fontWeight={600}>
                      我
                    </Typography>
                  </Avatar>
                )}
              </Stack>
            </Fade>
          ))}
          {loading && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar 
                sx={{ 
                  bgcolor: 'primary.main', 
                  width: 36, 
                  height: 36,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}
              >
                <SparkleIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Box
                sx={{
                  p: 2,
                  borderRadius: '18px 18px 18px 4px',
                  bgcolor: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                }}
              >
                <CircularProgress size={20} thickness={4} />
              </Box>
            </Stack>
          )}
        </Stack>
        <div ref={messagesEndRef} />
      </Box>
      )}

      {/* Input Area */}
      <Box 
        sx={{ 
          p: 2, 
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'white'
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-end">
          <TextField
            fullWidth
            size="medium"
            placeholder="输入你的问题..."
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading}
            multiline
            maxRows={4}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'grey.50',
                color: '#000000',
                '& .MuiInputBase-input': { color: '#000000' },
                '&:hover': {
                  bgcolor: 'grey.100',
                },
                '&.Mui-focused': {
                  bgcolor: 'white',
                },
                '& fieldset': {
                  borderColor: 'divider',
                },
                '&:hover fieldset': {
                  borderColor: 'primary.light',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />
          <IconButton
            onClick={handleSend}
            disabled={loading || !inputMessage.trim()}
            sx={{ 
              alignSelf: 'flex-end',
              bgcolor: 'primary.main',
              color: 'white',
              width: 48,
              height: 48,
              '&:hover': {
                bgcolor: 'primary.dark',
              },
              '&.Mui-disabled': {
                bgcolor: 'grey.300',
                color: 'grey.500',
              },
              transition: 'all 0.2s',
              boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
              '&:hover:not(.Mui-disabled)': {
                transform: 'scale(1.05)',
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
              }
            }}
          >
            <SendIcon />
          </IconButton>
        </Stack>
      </Box>
    </Paper>
  );
}

export default AIChat;
